import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { SPECIFICATION_LIST_DATA } from "../../models/data.model";
import { Widget, WidgetSubType } from "../../models/dashboard.model";
import { DatePipe } from "@angular/common";
import { SharedModule } from "../../../../shared/shared.module";
import { MatInputModule } from "@angular/material/input";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { Router } from "@angular/router";
import { EtatSpecification } from "../../../specifications/enums/etat-specif.enum";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { firstValueFrom } from "rxjs";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  SpecificationListDialogComponent,
  SpecificationListDialogData,
  SpecificationListDialogResult,
} from "./specification-list-dialog.component";

@Component({
  selector: "vl-specification-list",
  standalone: true,
  imports: [
    SharedModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    ChipsComponent,
    WidgetTitleComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-specification-list.component.html",
  styleUrl: "./widget-specification-list.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetSpecificationListComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  data = input<SPECIFICATION_LIST_DATA>();
  articleInfosFacade = inject(ArticlesInfosFacadeService);
  subType = signal<WidgetSubType>(WidgetSubType.SPECIFICATION_REDIGEE);
  widgetSubType = WidgetSubType;

  router = inject(Router);
  private dialogs = inject(DialogsService);

  specifications = computed(() => {
    if (!this.data() || !this.subType()) {
      return [];
    }
    return this.data()
      .specifications.filter((s) => {
        switch (this.subType()) {
          case WidgetSubType.SPECIFICATION_REDIGEE:
            return s.versions.some((v) => v.etat === EtatSpecification.REDIGEE);
          case WidgetSubType.SPECIFICATION_VALIDEE:
            return s.versions.some(
              (v) => v.etat === EtatSpecification.APPROUVEE,
            );
        }
      })
      .map((s) => {
        const denomination = s.articleId
          ? this.articleInfosFacade.getByArticleId(s.articleId)?.()
              ?.denomination
          : s?.denomination;
        let date;
        let nbVersion;
        switch (this.subType()) {
          case WidgetSubType.SPECIFICATION_REDIGEE: {
            const version = s.versions.find(
              (v) => v.etat === EtatSpecification.REDIGEE,
            );
            date = version?.dateRedaction;
            nbVersion = version?.version_nb;
            break;
          }
          case WidgetSubType.SPECIFICATION_VALIDEE: {
            const version = s.versions.find(
              (v) => v.etat === EtatSpecification.VALIDEE,
            );
            date = version?.dateValidation;
            nbVersion = version?.version_nb;
            break;
          }
        }
        return {
          id: s.id,
          denomination,
          articleId: s.articleId,
          nbVersion,
          date,
        };
      });
  });

  selectElem(elem: any) {
    if (elem.articleId) {
      this.router.navigateByUrl(`/specification/id/${elem.articleId}`);
    } else {
      this.router.navigateByUrl(`/etapes/id/${elem.id}`);
    }
  }

  /** Compteur global utilisé pour décider d'afficher le bouton "Voir tout". */
  totalCount = computed(() => this.data()?.specifications?.length ?? 0);

  async onSeeAll(): Promise<void> {
    const dialogData: SpecificationListDialogData = {
      data: this.data(),
      initialSubType:
        this.subType() === WidgetSubType.SPECIFICATION_VALIDEE
          ? "VALIDEE"
          : "REDIGEE",
    };
    const ref = this.dialogs.open(
      SpecificationListDialogComponent,
      undefined,
      undefined,
      dialogData,
    );
    const res = (await firstValueFrom(
      ref.afterClosed(),
    )) as SpecificationListDialogResult | null;
    if (res) this.selectElem(res);
  }
}
